import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { useTranslation, Trans } from "react-i18next";
import { usePublicPrimaryConfiguration } from "@/hooks/useConfig";

import bgSrc from "@/assets/add_to_home_screen/background.jpg";
import step1Src from "@/assets/add_to_home_screen/step_1.png";
import step2Src from "@/assets/add_to_home_screen/step_2.png";
import step3Src from "@/assets/add_to_home_screen/step_3.png";

export const AddToHomeScreen = () => {
  const { deferredPrompt, isIOS, isStandalone } = usePWAInstall();
  const [isVisible, setIsVisible] = useState(true);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const { t } = useTranslation();
  const { data: publicConfig } = usePublicPrimaryConfiguration();
  const brandName = publicConfig?.brand_name?.trim() || "Lazyly";

  useEffect(() => {
    // Show only if not standalone and hasn't been dismissed
    const isDismissed = localStorage.getItem("hide_add_to_home_screen");

    // Condition to show: Not installed + (Is iOS OR deferredPrompt is ready)
    // We remove the strict deferredPrompt requirement because we want to
    // fallback to popup if PWA prompt is not ready yet.
    if (!isStandalone && !isDismissed) {
      setIsVisible(true);
    }
  }, [isStandalone, isIOS, deferredPrompt]);

  const handleClose = () => {
    localStorage.setItem("hide_add_to_home_screen", "true");
    setIsVisible(false);
  };

  const handleAdd = async () => {
    if (isIOS) {
      // iOS doesn't support programmatic install prompt, show instructions popup
      setIsPopupOpen(true);
    } else if (deferredPrompt) {
      // Android/Chrome supports programmatic install prompt
      deferredPrompt.prompt();
      const { outcome } = await (deferredPrompt as any).userChoice;
      if (outcome === "accepted") {
        handleClose();
      }
    } else {
      // Fallback: If prompt is not available (e.g., PWA requirements not fully met)
      setIsPopupOpen(true);
    }
  };

  if (!isVisible) return null;

  return (
    <>
      <div
        className="w-full min-h-[48px] py-2 flex items-center justify-between px-4 shrink-0 gap-2"
        style={{
          backgroundImage: `url(${bgSrc})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <span className="text-[13px] sm:text-[14px] font-medium text-[#020909] leading-tight line-clamp-2 flex-1 pr-2">
          {t("pwa.add_to_home_screen", { brand: brandName })}
        </span>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={handleAdd}
            className="bg-white/80 backdrop-blur-md shadow-sm h-8 px-3 sm:px-4 rounded-lg flex items-center justify-center text-[#574ADB] text-[13px] sm:text-[14px] font-semibold"
            style={{
              boxShadow:
                "0px 2px 1.5px -0.5px rgba(0, 0, 0, 0.03), inset 0px 2px 3px 0px rgba(255, 255, 255, 0.03)",
            }}
          >
            {t("pwa.add")}
          </button>
          <button
            onClick={handleClose}
            className="flex items-center justify-center p-1 rounded-full hover:bg-black/5 shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-black" />
          </button>
        </div>
      </div>

      <Dialog open={isPopupOpen} onOpenChange={setIsPopupOpen}>
        <DialogContent
          hideCloseButton
          className="max-w-[480px] w-[calc(100%-2rem)] max-h-[80vh] flex flex-col rounded-3xl p-6 bg-white gap-0 shadow-xl border-none"
        >
          <DialogTitle className="text-[18px] font-bold text-center shrink-0 mb-6">
            {t("pwa.add_to_home_screen", { brand: brandName })}
          </DialogTitle>

          <div className="flex-1 overflow-y-auto flex flex-col gap-4 pb-6">
            <div className="flex flex-col gap-2">
              <p className="text-[14px] font-normal leading-relaxed text-[#0A0C11]">
                <Trans
                  i18nKey="pwa.step_1"
                  components={{ 1: <span className="font-bold" /> }}
                />
              </p>
              <img
                src={step1Src}
                className="w-full object-contain rounded-xl"
                alt="Step 1"
              />
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-[14px] font-normal leading-relaxed text-[#0A0C11]">
                <Trans
                  i18nKey="pwa.step_2"
                  components={{ 1: <span className="font-bold" /> }}
                />
              </p>
              <img
                src={step2Src}
                className="w-full object-contain rounded-xl"
                alt="Step 2"
              />
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-[14px] font-normal leading-relaxed text-[#0A0C11]">
                <Trans
                  i18nKey="pwa.step_3"
                  components={{ 1: <span className="font-bold" /> }}
                />
              </p>
              <img
                src={step3Src}
                className="w-full object-contain rounded-xl"
                alt="Step 3"
              />
            </div>
          </div>

          <button
            onClick={() => setIsPopupOpen(false)}
            className="shrink-0 w-full h-12 flex items-center justify-center rounded-xl border border-black/10 text-[14px] font-semibold text-[#0A0C11] hover:bg-gray-50 transition-colors"
          >
            {t("pwa.cancel")}
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
};
